import type { BlockedMutation } from '@/core/engine/progression';
import type { Match, Team, TeamId } from '@/core/schemas';
import { roundLabel, sideLabel } from '@/features/matches/matchPresentation';

import type { ReconcileOutcome } from './useAdminActions';

/** Plain-language summary of what a reconcile run changed. */
export function describeReconcile(outcome: ReconcileOutcome): string {
  const { applied } = outcome.progression;

  const bracket =
    applied === 0
      ? 'Der Turnierbaum war bereits aktuell.'
      : applied === 1
        ? 'Turnierbaum aktualisiert — ein Platz neu besetzt.'
        : `Turnierbaum aktualisiert — ${applied} Plätze neu besetzt.`;

  if (outcome.nextPhase === null) {
    return bracket;
  }

  return `${bracket} Nächste Phase geplant — ${outcome.nextPhase.matchCount} Spiele erzeugt.`;
}

/**
 * The warning for progression steps that were withheld.
 *
 * Names the affected matches instead of a bare count: the director has to walk
 * to a board and talk to people, and "Halbfinale 1: A gegen B" is what makes
 * that possible.
 */
export function describeBlocked(
  blocked: readonly BlockedMutation[],
  matches: readonly Match[],
  lookup: (teamId: TeamId) => Team | undefined,
): string | null {
  if (blocked.length === 0) {
    return null;
  }

  const byId = new Map(matches.map((match) => [match.id, match]));
  const affected = [...new Set(blocked.map((entry) => entry.mutation.matchId))]
    .map((matchId) => {
      const match = byId.get(matchId);
      if (match === undefined) {
        return null;
      }
      return `${roundLabel(match.roundKey)}: ${sideLabel(match.home, lookup)} gegen ${sideLabel(
        match.away,
        lookup,
      )}`;
    })
    .filter((label): label is string => label !== null);

  if (affected.length === 0) {
    return null;
  }

  return (
    `Die Korrektur betrifft ${affected.length === 1 ? 'ein Folgespiel' : 'Folgespiele'}, ` +
    `das bereits gespielt wird oder beendet ist — die Teilnehmer wurden dort nicht getauscht: ` +
    `${affected.join(' · ')}. Bitte dieses Spiel prüfen und gegebenenfalls das Ergebnis korrigieren.`
  );
}
